import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { UI_TIMINGS } from '../../config/appConfig';
import { useGeocodedAddresses } from '../../hooks/useGeocodedAddresses';
import MapMarkers from './map/MapMarkers';
import MovementTrail from './map/MovementTrail';
import MapControls from './map/MapControls';
import LocationListSidebar from './map/LocationListSidebar';
import { config } from '../../config/env';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  mapId: config.googleMapsMapId, // Required for Advanced Markers
  maxZoom: 22, // Maximum allowed by Google Maps API
  minZoom: 3,  // Prevent zooming out too far
};

// Keep libraries array as const outside component to prevent reloading
const libraries = ['marker'];

/**
 * LocationMapModal - Display event location(s) on a map
 *
 * @param {boolean} open - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {object|array} events - Single event or array of events with location data
 * @param {string} personName - Name of person
 * @param {string} personImage - Image URL of person
 */
export default function LocationMapModal({ open, onClose, events, personName, personImage }) {
  const [showAccuracy, setShowAccuracy] = useState(false);
  const [maxTimeGapMinutes, setMaxTimeGapMinutes] = useState(2);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedPersonForTrail, setSelectedPersonForTrail] = useState(null);
  const [showTrails, setShowTrails] = useState(true);
  const [map, setMap] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [navigationMode, setNavigationMode] = useState('time'); // 'time' or 'person'

  // Load Google Maps script with marker library
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  // Handle both single event and array of events (memoize to prevent infinite loops)
  const eventsWithLocation = useMemo(() => {
    const eventArray = Array.isArray(events) ? events : [events];
    return eventArray.filter(e => e?.location);
  }, [events]);

  // Count AI matches and manual matches, and get time range
  const matchCounts = useMemo(() => {
    const aiMatches = eventsWithLocation.filter(e => e.metadata?.matchSource === 'face_match').length;
    const manualMatches = eventsWithLocation.filter(e => e.metadata?.matchSource === 'manual_filter').length;

    // Get earliest and latest timestamps
    let earliestTime = null;
    let latestTime = null;

    if (eventsWithLocation.length > 0) {
      const times = eventsWithLocation.map(e => new Date(e.timestamp || e.createdAt).getTime());
      earliestTime = new Date(Math.min(...times));
      latestTime = new Date(Math.max(...times));
    }

    return { aiMatches, manualMatches, earliestTime, latestTime };
  }, [eventsWithLocation]);

  // Fetch addresses using custom hook
  const { addresses, loading: loadingAddresses } = useGeocodedAddresses(eventsWithLocation, open);

  const handleLocationClick = (index) => {
    setSelectedIndex(index);

    // If trails are off, show trail for this person only
    if (!showTrails) {
      const clickedEvent = eventsWithLocation[index];
      const personName = clickedEvent.metadata?.personName || clickedEvent.userId || 'Unknown';
      setSelectedPersonForTrail(personName.trim());
    }

    // Clear selection after animation
    setTimeout(() => setSelectedIndex(null), UI_TIMINGS.MAP_MARKER_ANIMATION);
  };

  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        // Enter fullscreen - request on the dialog element
        const dialogElement = document.querySelector('[role="dialog"]');
        if (dialogElement) {
          if (dialogElement.requestFullscreen) {
            await dialogElement.requestFullscreen();
          } else if (dialogElement.webkitRequestFullscreen) {
            // Safari/iOS
            await dialogElement.webkitRequestFullscreen();
          } else if (dialogElement.mozRequestFullScreen) {
            // Firefox
            await dialogElement.mozRequestFullScreen();
          } else if (dialogElement.msRequestFullscreen) {
            // IE11
            await dialogElement.msRequestFullscreen();
          }
          setIsFullscreen(true);
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          // Safari/iOS
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          // Firefox
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          // IE11
          await document.msExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  // Calculate center and bounds
  const center = useMemo(() => {
    if (eventsWithLocation.length === 0) return { lat: 0, lng: 0 };
    const firstEvent = eventsWithLocation[0];
    return {
      lat: firstEvent.location.latitude,
      lng: firstEvent.location.longitude
    };
  }, [eventsWithLocation]);

  const onLoad = React.useCallback((map) => {
    setMap(map);

    // Fit bounds to show all markers
    if (eventsWithLocation.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      eventsWithLocation.forEach(event => {
        bounds.extend({
          lat: event.location.latitude,
          lng: event.location.longitude
        });
      });
      map.fitBounds(bounds);
    }
  }, [eventsWithLocation]);

  const onUnmount = React.useCallback(() => {
    setMap(null);
  }, []);

  // Listen for fullscreen changes (user pressing ESC)
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (eventsWithLocation.length === 0) return null;

  if (loadError) {
    return null;
  }

  if (!isLoaded) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {personImage && (
              <Avatar src={personImage} alt={personName} sx={{ width: 48, height: 48 }} />
            )}
            <Box>
              <Typography variant="h6">{personName || 'Unknown'}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Match counts */}
                {matchCounts.aiMatches > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      {matchCounts.aiMatches}
                    </Typography>
                  </Box>
                )}
                {matchCounts.manualMatches > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {matchCounts.manualMatches}
                    </Typography>
                  </Box>
                )}

                {/* Time range */}
                {matchCounts.earliestTime && (
                  <Typography variant="caption" color="text.secondary">
                    •
                  </Typography>
                )}
                {matchCounts.earliestTime && (
                  <Typography variant="caption" color="text.secondary">
                    {matchCounts.earliestTime.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}{' '}
                    {matchCounts.earliestTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {matchCounts.latestTime &&
                     matchCounts.latestTime.getTime() !== matchCounts.earliestTime.getTime() && (
                      <> - {matchCounts.latestTime.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}{' '}
                      {matchCounts.latestTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                    )}
                    {matchCounts.latestTime &&
                     matchCounts.latestTime.getTime() === matchCounts.earliestTime.getTime() && (
                      <> (still open)</>
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapControls
              showAccuracy={showAccuracy}
              onAccuracyChange={setShowAccuracy}
              showTrails={showTrails}
              onTrailsChange={(checked) => {
                setShowTrails(checked);
                if (checked) {
                  setSelectedPersonForTrail(null); // Reset person filter when showing all trails
                }
              }}
              maxTimeGapMinutes={maxTimeGapMinutes}
              onMaxTimeGapChange={setMaxTimeGapMinutes}
              showTimeGap={eventsWithLocation.length > 1}
            />
            {eventsWithLocation.length > 1 && (
              <>
                <ToggleButtonGroup
                  value={navigationMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) {
                      setNavigationMode(newMode);
                    }
                  }}
                  size="small"
                  sx={{ height: 32 }}
                >
                  <ToggleButton value="time" aria-label="Navigate by time">
                    <AccessTimeIcon sx={{ fontSize: 18 }} />
                  </ToggleButton>
                  <ToggleButton value="person" aria-label="Navigate by person">
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Chip
                  label={`${eventsWithLocation.length} events`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </>
            )}
            <IconButton onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '100%', display: 'flex' }}>
        <Box sx={{ height: '100%', width: eventsWithLocation.length > 1 ? '75%' : '100%' }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={15}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions}
          >
            {(showTrails || selectedPersonForTrail) && (
              <MovementTrail
                events={eventsWithLocation}
                maxTimeGapMinutes={maxTimeGapMinutes}
                filterPersonName={selectedPersonForTrail}
              />
            )}

            <MapMarkers
              map={map}
              events={eventsWithLocation}
              personName={personName}
              showAccuracy={showAccuracy}
              addresses={addresses}
              loadingAddresses={loadingAddresses}
              selectedIndex={selectedIndex}
              onMarkerClick={handleLocationClick}
            />
          </GoogleMap>
        </Box>

        <LocationListSidebar
          events={eventsWithLocation}
          addresses={addresses}
          loadingAddresses={loadingAddresses}
          onLocationClick={handleLocationClick}
          selectedIndex={selectedIndex}
          navigationMode={navigationMode}
        />
      </DialogContent>
    </Dialog>
  );
}
